import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert, AlertTitle, List, ListItem, Typography, Box, CircularProgress,
} from '@mui/material'

import { useForm } from 'react-hook-form'
import ErrorIcon from '@mui/icons-material/Error'
import { MUIFormFileInput } from '../Form/FileInput/FormFileInput'
import ModalContainer from '../../Modals/ModalContainer'

function Error({ show, message }) {
  return show
    ? (
      <Box>
        <p style={{ color: '#d32f2f' }}>
          Import failed.
          {' '}
          {message}
          {' '}
          Please try again.
        </p>
      </Box>
    )
    : null
}

Error.propTypes = {
  show: PropTypes.bool,
  message: PropTypes.string,
}

// Ex: ('', 'Cat In The', 'Hat') => 'catInTheHat'
// Ex: ('The', 'Cat In The', 'Hat') => 'theCatInTheHat'
const getVariableName = (prefix, str, suffix) => `${prefix}${str
  .replace(/(?:^\w|[A-Z]|\b\w)/g, (w, i) => (!prefix && i === 0 ? w.toLowerCase() : w.toUpperCase()))
  .replace(/\s+/g, '')}${suffix}`

function Instructions({
  type,
  errors,
}) {
  return (
    <Typography component="div">
      {errors.length > 0
        && (
          <Box sx={{
            overflowY: 'auto', overflowX: 'hidden', maxHeight: '400px', maxWidth: '1000px', marginBottom: '24px',
          }}
          >
            <Alert
              icon={<ErrorIcon />}
              sx={{
                marginTop: '0px', backgroundColor: '#FDEDED', color: '#5F2120', '& .MuiAlert-icon': { color: '#d32f2f' },
              }}
              severity="error"
            >
              <AlertTitle sx={{ fontWeight: 'bold' }}>Please correct the following errors in the file and try again.</AlertTitle>
              <List sx={{ listStyleType: 'disc', listStylePosition: 'inside' }}>
                {errors.map((error) => (
                  <ListItem sx={{ display: 'list-item' }} key={error}>{error}</ListItem>
                ))}
              </List>
            </Alert>
          </Box>
        )}
      {errors.length === 0
        && (
          <>
            <Box>
              To import
              {' '}
              {type}
              , make sure your file adheres to the following standards:
            </Box>
            <ul style={{
              listStyle: 'inside', margin: '8px 0', padding: 0, display: 'flex', flexDirection: 'column', gap: '8px',
            }}
            >
              <li>File should be CSV format.</li>
            </ul>
          </>
        )}
    </Typography>
  )
}
Instructions.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.string),
  type: PropTypes.string,
}

function ImportModal({
  errors,
  processing,
  type,
  // handlers
  onClickClose,
  onClickSubmit,
  onFileChange,
}) {
  const fileKey = 'importFile'
  const ctaId = getVariableName('btnImport', type, 'BrowseFiles')
  const [localProcessing, setLocalProcessing] = useState(false)
  const [error, setError] = useState({ message: null, show: false })
  const { control, watch, handleSubmit } = useForm({
    defaultValues: {
      [fileKey]: null,
    },
    mode: 'onChange',
  })

  const onSubmitAsync = async (data) => {
    try {
      setLocalProcessing(true)
      await onClickSubmit(data)
    } catch (err) {
      setError({
        show: true,
        message: err.message || `Failed to import ${type}. Please try again.`,
      })
    } finally {
      setTimeout(() => setLocalProcessing(false), 1000)
    }
  }

  const noFileWatch = watch(fileKey) === null

  const importProcessing = processing || localProcessing

  return (
    <ModalContainer
      title={`Import ${type}`}
      maxWidth="xs"
      fullWidth="true"
      hideModal={() => { }}
      actions={importProcessing ? {} : {
        primaryAction: {
          label: 'Import',
          onClick: handleSubmit(onSubmitAsync),
          disabled: noFileWatch || importProcessing,
        },
        secondaryAction: {
          label: 'Cancel',
          onClick: () => onClickClose(),
          disabled: importProcessing,
        },
      }}
    >
      {importProcessing ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
          }}
        >
          <Typography sx={{ mb: 2 }}>Your import is processing</Typography>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Instructions errors={errors} type={type} />
          <Box sx={{ my: '20px' }}>
            <form onSubmit={handleSubmit(onSubmitAsync)}>
              <MUIFormFileInput
                name={fileKey}
                control={control}
                fileTypes=".csv"
                fileTypesDescription="CSV"
                processing={importProcessing}
                onChange={() => {
                  setError({ show: false, message: null })
                  onFileChange()
                }}
                gxid={ctaId}
                useNewStyling
                showDescription={false}
              />
              <Error show={error.show} message={error.message} />
            </form>
          </Box>
        </>
      )}
    </ModalContainer>
  )
}
ImportModal.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.string),
  processing: PropTypes.bool,
  type: PropTypes.string,
  onClickClose: PropTypes.func,
  onClickSubmit: PropTypes.func,
  onFileChange: PropTypes.func,
}

export default ImportModal
